import { injectKVStorage } from '@monofe/util';
import type { RefObject } from 'react';
import { useCallback } from 'react';

interface PositionStorage {
    scrollX: number;
    scrollY: number;
}

const _isPositionStorage = (object: unknown): object is PositionStorage => {
    const isNullOrUndefined = (value: unknown): boolean => value === null || value === undefined;

    if (isNullOrUndefined(object) || typeof object !== 'object') {
        return false;
    }
    const record: Partial<PositionStorage> = object as Partial<PositionStorage>;

    return !isNullOrUndefined(record.scrollX) && !isNullOrUndefined(record.scrollY);
};

const _readFromStorage = async (key: string): Promise<PositionStorage> => {
    const storage = injectKVStorage();
    const value = await storage.getItem(key);
    const position = _isPositionStorage(value) ? value : { scrollX: 0, scrollY: 0 };
    return position;
};

const _saveToStorage = async (key: string, position: PositionStorage) => {
    const storage = injectKVStorage();
    await storage.setItem(key, position);
};

const _deleteFromStorage = async (key: string) => {
    const storage = injectKVStorage();
    await storage.removeItem(key);
};

function usePreviousPosition(rowViewRef: RefObject<HTMLElement>, location: string) {
    const saveCurrentScrollPosition = useCallback(async () => {
        await _saveToStorage(location, {
            scrollX: rowViewRef.current?.scrollLeft ?? 0,
            scrollY: rowViewRef.current?.scrollTop ?? 0,
        });
    }, [rowViewRef, location]);

    const restoreScrollPosition = useCallback(async () => {
        const position = await _readFromStorage(location);
        rowViewRef.current?.scrollTo({
            left: position.scrollX,
            top: position.scrollY,
            behavior: 'auto',
        });
    }, [rowViewRef, location]);

    const clearScrollPosition = useCallback(async () => {
        await _deleteFromStorage(location);
    }, [location]);

    return { saveCurrentScrollPosition, restoreScrollPosition, clearScrollPosition };
}

export default usePreviousPosition;
